/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.22.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import {
    WIZARD_BACK_FAILED,
    WIZARD_BACK_SUCCESS,
    WIZARD_META_SUCCESS,
    WIZARD_NEXT_FAILED,
    WIZARD_NEXT_SUCCESS,
    WIZARD_RESTORE_FAILED,
    WIZARD_START_FAILED,
    WIZARD_START_SUCCESS,
} from '@/main/modules/wizard/wizardConstants';
import { HttpStatus } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { ICommonTranslation } from '@modules/internationalization/types';
import ModalErrorCommon from '@modules/modal/components/ModalError';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { wizardNextAction, wizardStorageSaveStepAction } from '@modules/wizard/wizardActions';
import { wizardModalTimeoutSaga, wizardStorageLoadStepsSaga } from '@modules/wizard/wizardSagas';
import { makeSelectWizardBasketSnapshotId, makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';

import { wizardOrderReportSetParamsAction } from './actions';
import { EWizardOrderReportSteps, WIZARD_ORDER_REPORT_HANDLE_NEXT } from './constants';

/*
 * -------------------------------------------
 *           INITIALIZING
 * -------------------------------------------
 */

export function* wizardOrderReportMetaSaga(/* action: TWizardMetaSuccessAction */): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardOrderReportSteps.ReportSelectionDataStep:
            break;
        case EWizardOrderReportSteps.ReportSelectionParametersStep:
            break;
        case EWizardOrderReportSteps.ReportConfirmStep:
            break;
    }
}

/*
 * -------------------------------------------
 *       SEND DATA ON NEXT STEP
 * -------------------------------------------
 */

export function* wizardOrderReportHandleNextSaga(): SagaIterator {
    yield put(wizardNextAction({}));
}

export function* wizardOrderReportHandleNextSuccessSaga(/* action: TWizardNextSuccessAction */): SagaIterator {
    const stepName = yield select(makeSelectWizardStepName());

    switch (stepName) {
        case EWizardOrderReportSteps.ReportSelectionDataStep:
            break;
        case EWizardOrderReportSteps.ReportSelectionParametersStep:
            break;
        case EWizardOrderReportSteps.ReportConfirmStep:
            break;
    }
}

/*
 * -------------------------------------------
 *       STORAGE
 * -------------------------------------------
 */

export function* wizardOrderReportLoadDataFromStorageSaga(): SagaIterator {
    const localStorageState = yield call(wizardStorageLoadStepsSaga);
    const basketSnapshotId = yield select(makeSelectWizardBasketSnapshotId());
    const localStorageParams = localStorageState[basketSnapshotId];

    if (localStorageParams) {
        yield put(wizardOrderReportSetParamsAction(localStorageParams)); //! TODO: Check with data
    }
}

/*
 * -------------------------------------------
 *       SAVE DATA TO STORAGE
 * -------------------------------------------
 */

export function* wizardOrderReportStorageSaveSaga(): SagaIterator {
    const params = {};
    yield put(wizardStorageSaveStepAction({ params }));
}

/*
 * -------------------------------------------
 *              Failure Handler
 * -------------------------------------------
 */

export function* wizardOrderReportFailedSaga(action: any): SagaIterator {
    const errorCode = action.payload?.response?.error?.code;
    const httpStatus = action.payload?.code;
    let title: string;

    const translationCommon: ICommonTranslation = yield select(makeSelectTranslationCommon());

    if (httpStatus === HttpStatus.NOT_ACCEPTABLE) {
        yield call(wizardModalTimeoutSaga);
        return;
    } else if (httpStatus === HttpStatus.NOT_FOUND) {
        return;
    } else if (errorCode) {
        console.error(errorCode);
    }

    yield put(
        modalOpen({
            render: (props: IModalRenderProps) =>
                createElement(ModalErrorCommon, {
                    onCloseModal: props.onCloseModal,
                    title,
                    translation: translationCommon.modal,
                }),
        }),
    );
}

/*
 * -------------------------------------------
 *       Wizard Sagas
 * -------------------------------------------
 */

export function* wizardOrderReportSagas() {
    yield all([
        takeEvery(WIZARD_START_SUCCESS, wizardOrderReportStorageSaveSaga),
        takeEvery(WIZARD_META_SUCCESS, wizardOrderReportMetaSaga),
        takeEvery(WIZARD_ORDER_REPORT_HANDLE_NEXT, wizardOrderReportHandleNextSaga),

        takeEvery(WIZARD_NEXT_SUCCESS, wizardOrderReportHandleNextSuccessSaga),
        takeEvery([WIZARD_NEXT_SUCCESS, WIZARD_BACK_SUCCESS], wizardOrderReportLoadDataFromStorageSaga),

        takeEvery(
            [WIZARD_BACK_FAILED, WIZARD_NEXT_FAILED, WIZARD_RESTORE_FAILED, WIZARD_START_FAILED],
            wizardOrderReportFailedSaga,
        ),
    ]);
}
